import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Lightbulb, Package2, Scale, Search } from "lucide-react";
import { api, fmtTL, QUALITY_OPTIONS } from "../lib/api";
import QualityBadge from "../components/QualityBadge";
import { useShop } from "../context/ShopContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SmartCategoryPicker from "../components/SmartCategoryPicker";

export default function Catalog() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState(params.get("q") || "");
    const [ads, setAds] = useState([]);
    const [suggestion, setSuggestion] = useState(null);
    const [loading, setLoading] = useState(true);
    const { compareIds, toggleCompare } = useShop();
    const cat = params.get("kategori") || "all";
    const sort = params.get("sort") || "relevance";
    const quality = params.get("kalite") || "all";
    const subCats = categories.filter((c) => c.parent_id === cat);
    const mainCats = categories.filter((c) => !c.parent_id);

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (cat !== "all") api.get(`/ads?placement=${cat}`).then((r) => setAds(r.data)).catch(() => setAds([]));
        else setAds([]);
    }, [cat]);

    useEffect(() => {
        const qp = params.get("q") || "";
        if (qp !== q) setQ(qp);
    }, [params]);

    useEffect(() => {
        setLoading(true);
        const qs = new URLSearchParams();
        if (cat !== "all") qs.set("category_id", cat);
        if (q) qs.set("q", q);
        if (sort !== "relevance") qs.set("sort", sort);
        if (quality !== "all") qs.set("quality", quality);
        api.get(`/products?${qs}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
    }, [cat, q, sort, quality]);

    useEffect(() => {
        if (!q || q.trim().length < 2) { setSuggestion(null); return; }
        const t = setTimeout(() => {
            api.get(`/category-conflicts/suggest?q=${encodeURIComponent(q)}`).then((r) => setSuggestion(r.data)).catch(() => setSuggestion(null));
        }, 350);
        return () => clearTimeout(t);
    }, [q]);

    return (
        <div className="space-y-6" data-testid="catalog-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Katalog</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{products.length} ürün — tedarikçi fiyatlarını karşılaştırın</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Ürün ara..."
                            className="w-56 pl-9"
                            data-testid="catalog-search-input"
                        />
                    </div>
                    <Select value={sort} onValueChange={(v) => setParams((p) => { p.set("sort", v); return p; })}>
                        <SelectTrigger className="w-44" data-testid="catalog-sort"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="relevance">Önerilen</SelectItem>
                            <SelectItem value="price_asc">Fiyat (Artan)</SelectItem>
                            <SelectItem value="price_desc">Fiyat (Azalan)</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={quality} onValueChange={(v) => setParams((p) => { if (v === "all") { p.delete("kalite"); } else { p.set("kalite", v); } return p; })}>
                        <SelectTrigger className="w-40" data-testid="catalog-quality-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Kalite: Tümü</SelectItem>
                            {QUALITY_OPTIONS.map((qo) => <SelectItem key={qo} value={qo}>{qo}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div data-testid="category-filter" className="max-w-md">
                <SmartCategoryPicker
                    categories={categories}
                    value={cat}
                    onChange={(id) => setParams((p) => { if (id === "all") { p.delete("kategori"); } else { p.set("kategori", id); } return p; })}
                    allowAll
                    allLabel="Tüm Kategoriler"
                    tid="cat-filter"
                />
            </div>

            {suggestion && (
                <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-saffron-300 bg-saffron-50 p-3.5" data-testid="category-suggestion-banner">
                    <Lightbulb className="h-4 w-4 shrink-0 text-saffron-600" />
                    <p className="text-sm">
                        <span className="font-semibold">"{suggestion.term}"</span> için önerilen ana kategori:{" "}
                        {suggestion.recommended_category_id ? (
                            <button
                                type="button"
                                className="font-semibold text-terra-700 underline underline-offset-2 hover:text-terra-800"
                                onClick={() => setParams((p) => { p.set("kategori", suggestion.recommended_category_id); return p; })}
                                data-testid="suggestion-recommended-link"
                            >
                                {suggestion.recommended_name}
                            </button>
                        ) : (
                            <span className="font-semibold">{suggestion.recommended_name}</span>
                        )}
                    </p>
                    {suggestion.related?.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                            <span>İlgili:</span>
                            {suggestion.related.map((r) => (
                                <button
                                    key={r.code}
                                    type="button"
                                    className="rounded-full border bg-white px-2.5 py-0.5 text-xs font-medium transition-colors hover:border-terra-500/50 hover:text-terra-700"
                                    onClick={() => r.category_id && setParams((p) => { p.set("kategori", r.category_id); return p; })}
                                    data-testid={`suggestion-related-${r.code}`}
                                >
                                    {r.name}
                                </button>
                            ))}
                        </div>
                    )}
                    <button type="button" onClick={() => setSuggestion(null)} className="ml-auto text-xs text-muted-foreground hover:text-foreground" data-testid="suggestion-dismiss">Kapat</button>
                </div>
            )}

            {ads.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2" data-testid="category-ads">
                    {ads.map((ad) => (
                        <div key={ad.ad_id} className="relative overflow-hidden rounded-2xl border shadow-sm" data-testid={`ad-${ad.ad_id}`}>
                            {ad.type === "image" && <img src={ad.media_url} alt={ad.title} className="h-36 w-full object-cover" />}
                            {ad.type === "youtube" && (
                                <div className="aspect-video">
                                    <iframe className="h-full w-full" src={ad.media_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")} title={ad.title} allowFullScreen />
                                </div>
                            )}
                            {ad.type === "video" && <video src={ad.media_url} controls className="h-36 w-full object-cover" />}
                            {ad.type !== "youtube" && (
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-stone-950/80 to-transparent p-3">
                                    <p className="text-sm font-bold text-white">{ad.title}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
                    {[...Array(8)].map((_, i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-muted" />)}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4" data-testid="product-grid">
                    {products.map((p, i) => (
                        <div key={p.product_id} className="group animate-fade-up overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow hover:shadow-md" style={{ animationDelay: `${Math.min(i, 8) * 0.04}s` }} data-testid={`product-card-${p.product_id}`}>
                            <Link to={`/panel/urun/${p.product_id}`} className="block">
                                <div className="relative overflow-hidden">
                                    <img src={p.image} alt={p.name} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                                    {p.total_stock > 0 ? (
                                        <Badge className="absolute left-2 top-2 bg-emerald-600/90 text-white border-0 text-[10px]">Stokta</Badge>
                                    ) : (
                                        <Badge className="absolute left-2 top-2 bg-red-600/90 text-white border-0 text-[10px]">Tükendi</Badge>
                                    )}
                                    {p.has_campaign && (
                                        <Badge className="absolute right-2 top-2 border-0 bg-amber-500/95 text-[10px] font-bold text-white" data-testid={`campaign-badge-${p.product_id}`}>Kampanya</Badge>
                                    )}
                                </div>
                                <div className="p-4">
                                    <p className="font-display text-sm font-bold leading-snug line-clamp-2 min-h-10">{p.name}</p>
                                    {p.quality && <div className="mt-1"><QualityBadge quality={p.quality} tid={p.product_id} /></div>}
                                    <div className="mt-2 flex items-baseline gap-1.5">
                                        <span className="font-display text-lg font-extrabold text-terra-600">{p.min_price != null ? fmtTL(p.min_price) : "—"}</span>
                                        <span className="text-xs text-muted-foreground">/ {p.unit}'den başlayan (KDV dahil)</span>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">{p.offer_count} tedarikçi teklifi</p>
                                </div>
                            </Link>
                            <div className="flex gap-2 px-4 pb-4">
                                <Button asChild size="sm" className="flex-1 bg-terra-600 hover:bg-terra-700 text-white" data-testid={`product-view-${p.product_id}`}>
                                    <Link to={`/panel/urun/${p.product_id}`}><Package2 className="mr-1.5 h-3.5 w-3.5" /> Teklifler</Link>
                                </Button>
                                <Button
                                    size="sm"
                                    variant={compareIds.includes(p.product_id) ? "default" : "outline"}
                                    onClick={() => toggleCompare(p.product_id)}
                                    className={compareIds.includes(p.product_id) ? "bg-saffron-400 text-stone-900 hover:bg-saffron-500" : ""}
                                    data-testid={`compare-toggle-${p.product_id}`}
                                >
                                    <Scale className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    {!products.length && <p className="col-span-full py-16 text-center text-sm text-muted-foreground">Ürün bulunamadı.</p>}
                </div>
            )}
        </div>
    );
}
